import React from "react";
import {
  Box,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  LoadingButton,
  Modal,
  ScrollableWithGradients,
} from "@rentablo/components";
import LockIcon from "@icons/myicons/lock.svg?react";
import ImportBackground from "./import-background.svg?react";
import { useAccountReview } from "./hooks/useAccountReview";
import { OptionalAccountRow } from "./OptionalAccountRow";
import { RequiredAccountRow } from "./RequiredAccountRow";

export const AccountReviewModal = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    isOpen,
    optionalAccounts,
    requiredRows,
    selectedIds,
    toggleAccount,
    confirm,
    close,
    isConfirming,
  } = useAccountReview();

  return (
    <Modal
      isOpen={isOpen}
      onCloseButtonClick={close}
      title={t("accountReview.title")}
      subtitle={t("accountReview.subtitle")}
      width={720}
      subtitleVariant="body4"
      titleVariant="h4"
      titleMargin={6}
      borderRadius="16px"
      height={isMobile ? "fit-content" : "auto"}
      maxHeight={isMobile ? "90vh" : "80vh"}
      position={isMobile ? "bottom" : "centered"}
      animation={isMobile ? "slide-up" : "none"}
      contentPadding={isMobile ? "24px 20px" : "32px 36px"}
      isRelative
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        overflow="hidden"
        sx={{
          pointerEvents: "none",
          color: theme.palette.primary.main,
          width: "100%",
        }}
      >
        <ImportBackground />
      </Box>
      <Divider sx={{ borderColor: "border.secondary", mb: 6 }} />
      <Box mb={5}>
        <Typography variant="body4" color="text.secondary">
          {t("accountReview.description")}
        </Typography>
      </Box>
      <ScrollableWithGradients>
        {optionalAccounts.map((account) => (
          <OptionalAccountRow
            key={account.id}
            account={account}
            isSelected={selectedIds.has(account.id)}
            onToggle={() => toggleAccount(account.id)}
          />
        ))}

        {requiredRows.length > 0 && (
          <>
            <Box
              display="flex"
              alignItems="center"
              gap={3}
              mt={6}
              mb={5}
              color="text.secondary"
            >
              <LockIcon width="20px" height="20px" />
              <Typography variant="body3" color="text.secondary">
                {t("accountReview.requiredSection")}
              </Typography>
            </Box>
            {requiredRows.map((row, index) => (
              <RequiredAccountRow
                key={row.account.id}
                account={row.account}
                isReference={row.isReference}
                isLast={index === requiredRows.length - 1}
              />
            ))}
          </>
        )}
      </ScrollableWithGradients>

      <Box position="sticky" bottom={0} pt={6} pb={2}>
        <LoadingButton
          variant="contained"
          color="primary"
          fullWidth
          onClick={confirm}
          isFetching={isConfirming}
          data-testid="account-review-confirm"
        >
          {t("accountReview.confirm")}
        </LoadingButton>
      </Box>
    </Modal>
  );
};
